import React, { useState } from 'react';
import { getPushState, enablePush } from '../utils/push';

// A slim banner that helps the user turn on device notifications.
//
// Three cases:
//  • iPhone/iPad in a normal Safari TAB → web push is impossible there (Apple
//    only delivers push to a home-screen web app), so guide them to
//    "Add to Home Screen".
//  • permission 'default' → an "Enable notifications" button; the permission
//    prompt fires from THIS click (browsers suppress an auto-requested prompt).
//  • permission 'denied' → tell them how to unblock in site settings.
// Hidden when already granted / unsupported (desktop) / dismissed this session.
export default function PushPrompt() {
  const [state, setState] = useState(() => getPushState());
  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem('pushPromptDismissed') === '1'
  );
  const [busy, setBusy] = useState(false);

  if (dismissed) return null;

  const ua = navigator.userAgent || '';
  const isIOS = /iPad|iPhone|iPod/.test(ua) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1); // iPadOS
  const isStandalone =
    window.navigator.standalone === true ||
    (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches);

  const onEnable = async () => {
    setBusy(true);
    const result = await enablePush();
    setBusy(false);
    setState(getPushState());
    if (result !== 'default') setDismissed(true); // granted or denied → done deciding
  };
  const onDismiss = () => {
    sessionStorage.setItem('pushPromptDismissed', '1');
    setDismissed(true);
  };

  const wrap = {
    background: '#eff6ff',
    borderBottom: '1px solid #bfdbfe',
    padding: '10px 16px',
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    fontSize: 'var(--font-size-sm)', // follows the app's scale (phone: the dashboard's body size)
    color: '#1e3a5f',
    flexWrap: 'wrap',
  };
  const primaryBtn = {
    marginLeft: 'auto',
    padding: '6px 14px',
    background: '#2563eb',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    cursor: busy ? 'default' : 'pointer',
    fontWeight: 600,
    opacity: busy ? 0.7 : 1,
  };
  const dismissBtn = {
    padding: '6px 10px',
    background: 'transparent',
    color: '#64748b',
    border: 'none',
    cursor: 'pointer',
  };

  // iPhone/iPad in a Safari tab: push only works once added to the Home Screen.
  if (isIOS && !isStandalone && !state.supported) {
    return (
      <div style={wrap}>
        <span>
          📲 To get notifications on iPhone: tap the <strong>Share</strong> button, choose{' '}
          <strong>Add to Home Screen</strong>, then open <strong>CNK CRM</strong> from your Home
          Screen and turn on notifications.
        </span>
        <button onClick={onDismiss} style={{ ...dismissBtn, marginLeft: 'auto' }}>Dismiss</button>
      </div>
    );
  }

  // Desktop/Android that can't do push, or already granted → nothing to show.
  if (!state.supported || state.permission === 'granted') return null;

  // Notifications were previously BLOCKED — can't re-prompt; guide to settings.
  if (state.permission === 'denied') {
    return (
      <div style={{ ...wrap, background: '#fef2f2', borderBottom: '1px solid #fecaca', color: '#7f1d1d' }}>
        <span>
          🔕 Notifications are blocked for this site. To get device alerts, click the lock/ⓘ icon
          in the address bar → <strong>Notifications</strong> → <strong>Allow</strong>, then reload.
        </span>
        <button onClick={onDismiss} style={{ ...dismissBtn, marginLeft: 'auto' }}>Dismiss</button>
      </div>
    );
  }

  // permission === 'default' — offer to enable (the prompt fires from this click).
  return (
    <div style={wrap}>
      <span>
        🔔 Turn on device notifications to get alerts for new tasks &amp; messages — even when this
        tab is closed.
      </span>
      <button onClick={onEnable} disabled={busy} style={primaryBtn}>
        {busy ? 'Enabling…' : 'Enable notifications'}
      </button>
      <button onClick={onDismiss} style={dismissBtn}>Not now</button>
    </div>
  );
}
